import { db } from '../../services/firebase';
import { doc, setDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';

/**
 * Service for creating and managing forwarding email addresses
 * This utility provides functionality for creating and managing email forwarding addresses
 */
const PrivacyEmailGenerator = {
  /**
   * Generate a new forwarding email address (SYNCHRONOUS VERSION)
   * @param {string} baseEmail - The user's original email
   * @returns {string} - The generated forwarding email
   */
  generateForwardingEmail: (baseEmail) => {
    try {
      // Extract username from base email
      const username = baseEmail.split('@')[0];
      
      // Generate unique identifier
      const uniqueId = Math.random().toString(36).substring(2, 8);
      
      // Create forwarding email
      const forwardingEmail = `${username}.${uniqueId}@artifact.forward.com`;
      
      return forwardingEmail;
    } catch (error) {
      console.error('Error generating forwarding email:', error);
      // Fallback to simple generation if error occurs
      const username = baseEmail.split('@')[0];
      const uniqueId = Math.random().toString(36).substring(2, 8);
      const fallbackEmail = `${username}.${uniqueId}@artifact.forward.com`;
      return fallbackEmail;
    }
  },

  /**
   * Store forwarding email in Firebase (separate async function)
   * @param {string} forwardingEmail - The forwarding email
   * @param {string} baseEmail - The user's original email
   * @param {string} userId - The user's ID for storage
   * @returns {Promise<boolean>} - Success status
   */
  storeForwardingEmail: async (forwardingEmail, baseEmail, userId) => {
    try {
      if (!userId) {
        console.error('Cannot store forwarding email: userId is undefined');
        return false;
      }

      const emailDocRef = doc(db, 'emailForwarding', forwardingEmail);
      await setDoc(emailDocRef, {
        sourceEmail: forwardingEmail,
        targetEmail: baseEmail,
        userId: userId,
        createdAt: new Date(),
        active: true
      });
      
      return true;
    } catch (firestoreError) {
      console.error('Firestore error:', firestoreError);
      return false;
    }
  },
  
  /**
   * List all forwarding emails for a user
   * @param {string} userId - The user's ID
   * @returns {Promise<Array>} - List of forwarding email objects
   */
  listUserForwardingEmails: async (userId) => {
    try {
      // Using Firebase v9 syntax
      const emailsRef = collection(db, 'emailForwarding');
      const q = query(emailsRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error listing forwarding emails:', error);
      return [];
    }
  },
  
  /**
   * Deactivate a forwarding email
   * @param {string} forwardingEmail - The forwarding email to deactivate
   * @returns {Promise<void>}
   */
  deactivateForwardingEmail: async (forwardingEmail) => {
    try {
      // Using Firebase v9 syntax
      const emailDocRef = doc(db, 'emailForwarding', forwardingEmail);
      await updateDoc(emailDocRef, {
        active: false,
        deactivatedAt: new Date()
      });
      return true;
    } catch (error) {
      console.error('Error deactivating forwarding email:', error);
      return false;
    }
  },
  
  /**
   * Create a one-time use disposable email for a specific service
   * @param {string} baseEmail - The user's original email
   * @param {string} userId - The user's ID
   * @param {string} serviceName - The service this email is for (e.g. "newsletter-signup")
   * @returns {Promise<string>} - The generated disposable email
   */
  createDisposableEmail: async (baseEmail, userId, serviceName) => {
    try {
      // Extract username from base email
      const username = baseEmail.split('@')[0];
      
      // Sanitize service name for email use
      const sanitizedService = serviceName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      
      // Generate unique identifier
      const uniqueId = Math.random().toString(36).substring(2, 5);
      
      // Create disposable email
      const disposableEmail = `${username}.${sanitizedService}.${uniqueId}@artifact.forward.com`;
      
      // Store mapping in database with metadata using Firebase v9 syntax
      try {
        const emailDocRef = doc(db, 'emailForwarding', disposableEmail);
        await setDoc(emailDocRef, {
          sourceEmail: disposableEmail,
          targetEmail: baseEmail,
          userId: userId,
          service: serviceName,
          disposable: true,
          usageCount: 0,
          createdAt: new Date(),
          active: true
        });
      } catch (firestoreError) {
        console.error('Firestore error:', firestoreError);
      }
      
      return disposableEmail;
    } catch (error) {
      console.error('Error creating disposable email:', error);
      // Fallback
      const username = baseEmail.split('@')[0];
      const uniqueId = Math.random().toString(36).substring(2, 5);
      return `${username}.${uniqueId}@artifact.forward.com`;
    }
  }
};

export default PrivacyEmailGenerator;